import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, SlidersHorizontal, Sprout, Trash2 } from 'lucide-react';
import { Button, Chip, Select, Stat } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { KG_POR_SACA } from '../labels';
import {
  useProdutividadeCliente,
  useDeleteProdutividade,
} from '../api';
import type { Produtividade } from '../schemas';
import {
  CULTURA_LABELS, FONTE_LABELS, TECNOLOGIA_LABELS, CULTIVO_LABELS,
  MAO_DE_OBRA_LABELS, FERTILIDADE_LABELS, AGRICULTURA_LABELS,
  IRRIGACAO_LABELS, TIPO_POSSE_LABELS, STATUS_OPTIONS, CULTURA_OPTIONS,
} from '../labels';
import { StatusChip } from './StatusChip';
import { ProdutividadeEditModal } from './ProdutividadeEditModal';

interface Props {
  cpf: string;
  clienteId: number;
}

export function ProdutividadeTable({ cpf, clienteId }: Props) {
  const { data: registros, isLoading, error } = useProdutividadeCliente(cpf);
  const del = useDeleteProdutividade(cpf);

  const [filterCultura, setFilterCultura] = useState<string>('');
  const [filterSafra, setFilterSafra] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalMode, setModalMode] = useState<
    | { kind: 'create'; cpf: string; clienteId: number }
    | { kind: 'edit'; cpf: string; clienteId: number; record: Produtividade }
    | null
  >(null);

  const activeCount = (filterCultura ? 1 : 0) + (filterSafra ? 1 : 0) + (filterStatus ? 1 : 0);

  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  function limparFiltros() {
    setFilterCultura('');
    setFilterSafra('');
    setFilterStatus('');
  }

  const safras = useMemo(() => {
    const set = new Set<string>();
    (registros ?? []).forEach((r) => set.add(r.safra));
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [registros]);

  const filtered = useMemo(() => {
    return (registros ?? []).filter((r) => {
      if (filterCultura && r.cultura !== filterCultura) return false;
      if (filterSafra && r.safra !== filterSafra) return false;
      if (filterStatus && r.status !== filterStatus) return false;
      return true;
    });
  }, [registros, filterCultura, filterSafra, filterStatus]);

  const counts = useMemo(() => {
    const total = registros?.length ?? 0;
    const conf = registros?.filter((r) => r.status === 'confirmado').length ?? 0;
    const pend = registros?.filter((r) => r.status === 'pendente').length ?? 0;
    const ajus = registros?.filter((r) => r.status === 'ajustar').length ?? 0;
    return { total, conf, pend, ajus };
  }, [registros]);

  // Média sc/ha das últimas 3 safras por cultura. Se uma safra tem múltiplos
  // registros (talhões), tira média intra-safra primeiro pra não enviesar.
  const { mediaSoja, mediaMilho } = useMemo(() => {
    function calc(cultura: string) {
      const itens = (registros ?? []).filter((r) => r.cultura === cultura && r.sc_ha != null);
      if (itens.length === 0) return null;
      const porSafra = new Map<string, number[]>();
      for (const r of itens) {
        const arr = porSafra.get(r.safra) ?? [];
        arr.push(Number(r.sc_ha));
        porSafra.set(r.safra, arr);
      }
      const avgPorSafra = [...porSafra.entries()].map(([safra, scs]) => ({
        safra,
        avg: scs.reduce((s, v) => s + v, 0) / scs.length,
      }));
      avgPorSafra.sort((a, b) => b.safra.localeCompare(a.safra));
      const top = avgPorSafra.slice(0, 3);
      const overall = top.reduce((s, x) => s + x.avg, 0) / top.length;
      return {
        sc: overall,
        kg: overall * KG_POR_SACA,
        safrasUsadas: top.length,
        safrasIds: top.map((t) => t.safra),
      };
    }
    return { mediaSoja: calc('soja'), mediaMilho: calc('milho') };
  }, [registros]);

  function handleDelete(r: Produtividade) {
    const cultura = CULTURA_LABELS[r.cultura] ?? r.cultura;
    if (!window.confirm(`Excluir registro de ${cultura} · ${r.safra}?`)) return;
    del.mutate(r.id, {
      onError: (err) => alert(`Falha ao excluir: ${err instanceof Error ? err.message : 'erro'}`),
    });
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Stats + ações (Filtrar por + Adicionar registro) */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Chip tone="neutral" size="sm">{counts.total} {counts.total === 1 ? 'registro' : 'registros'}</Chip>
          {counts.conf > 0 && <Chip tone="ok" size="sm">{counts.conf} confirmado{counts.conf > 1 ? 's' : ''}</Chip>}
          {counts.pend > 0 && <Chip tone="warn" size="sm">{counts.pend} pendente{counts.pend > 1 ? 's' : ''}</Chip>}
          {counts.ajus > 0 && <Chip tone="danger" size="sm">{counts.ajus} ajustar</Chip>}

          <div className="ml-auto flex items-center gap-3">
            {(registros?.length ?? 0) > 0 && (
              <div ref={filterWrapRef} className="relative">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-[0.82rem]',
                    'border border-border transition-colors',
                    activeCount > 0 || filtersOpen
                      ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                      : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
                  )}
                >
                  <SlidersHorizontal size={13} strokeWidth={1.5} />
                  Filtrar por
                  {activeCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 ml-1 rounded-full bg-accent text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
                      {activeCount}
                    </span>
                  )}
            </button>

            {filtersOpen && (
              <div
                className={cn(
                  'absolute top-[calc(100%+6px)] left-0 z-50',
                  'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                  'p-4 min-w-[280px] flex flex-col gap-3',
                )}
              >
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="prod-flt-cultura"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Cultura
                  </label>
                  <Select
                    id="prod-flt-cultura"
                    value={filterCultura}
                    onChange={(e) => setFilterCultura(e.target.value)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todas</option>
                    {CULTURA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="prod-flt-safra"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Safra
                  </label>
                  <Select
                    id="prod-flt-safra"
                    value={filterSafra}
                    onChange={(e) => setFilterSafra(e.target.value)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todas</option>
                    {safras.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="prod-flt-status"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Status
                  </label>
                  <Select
                    id="prod-flt-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todos</option>
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </div>

                <button
                  type="button"
                  onClick={limparFiltros}
                  disabled={activeCount === 0}
                  className={cn(
                    'self-start text-[0.78rem] text-accent bg-transparent border-0 p-0',
                    'hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline',
                  )}
                >
                  Limpar filtros
                </button>
              </div>
            )}
              </div>
            )}
            <Button onClick={() => setModalMode({ kind: 'create', cpf, clienteId })}>
              <Plus size={14} strokeWidth={1.5} /> Adicionar registro
            </Button>
          </div>
        </div>

        {/* Stat cards — média sc/ha das últimas 3 safras por cultura */}
        {(mediaSoja || mediaMilho) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mediaSoja && (
              <Stat
                label="Média sc/ha — Soja"
                value={`${mediaSoja.sc.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} sc/ha`}
                hint={`${mediaSoja.kg.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kg/ha`}
                icon={<Sprout size={20} strokeWidth={1.5} className="text-[var(--success-fg)]" />}
                tone="ok"
              />
            )}
            {mediaMilho && (
              <Stat
                label="Média sc/ha — Milho"
                value={`${mediaMilho.sc.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} sc/ha`}
                hint={`${mediaMilho.kg.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kg/ha`}
                icon={<Sprout size={20} strokeWidth={1.5} className="text-accent" />}
                tone="accent"
              />
            )}
          </div>
        )}

          {isLoading && <div className="module-loading">Carregando…</div>}
          {error && (
            <div className="empty-state">
              <p className="empty-state-desc">Falha ao carregar: {error instanceof Error ? error.message : 'erro'}</p>
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="empty-state">
              <Sprout size={32} strokeWidth={1.5} className="empty-state-icon" />
              <p className="empty-state-title">Sem registros de produtividade</p>
              <p className="empty-state-desc">
                {(registros?.length ?? 0) === 0
                  ? 'Cadastre a primeira safra clicando em "+ Adicionar registro".'
                  : 'Nenhum registro bate com os filtros selecionados.'}
              </p>
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center' }}>Matrícula</th>
                    <th>Lote / Gleba</th>
                    <th style={{ textAlign: 'center' }}>Posse</th>
                    <th style={{ textAlign: 'center' }}>Cultura</th>
                    <th style={{ textAlign: 'center' }}>Safra</th>
                    <th style={{ textAlign: 'center' }}>Sc/ha</th>
                    <th style={{ textAlign: 'center' }}>Kg/ha</th>
                    <th>Fonte</th>
                    <th style={{ textAlign: 'center' }}>Tecnologia</th>
                    <th style={{ textAlign: 'center' }}>Cultivo</th>
                    <th style={{ textAlign: 'center' }}>Mão de obra</th>
                    <th style={{ textAlign: 'center' }}>Fertilidade</th>
                    <th style={{ textAlign: 'center' }}>Agricultura</th>
                    <th style={{ textAlign: 'center' }}>Irrigação</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const loteGleba = [r.lote, r.gleba].filter(Boolean).join(' · ') || '—';
                    const matricula = r.matricula ?? r.imovel_matricula_ref ?? null;
                    return (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'center' }}>
                        {matricula ? (
                          <Chip tone="info" size="sm">{matricula}</Chip>
                        ) : '—'}
                      </td>
                      <td>{loteGleba}</td>
                      <td style={{ textAlign: 'center' }}>
                        {r.tipo_posse ? (
                          <Chip tone={r.tipo_posse === 'proprio' ? 'ok' : 'warn'} size="sm">
                            {TIPO_POSSE_LABELS[r.tipo_posse]}
                          </Chip>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Chip tone={r.cultura === 'soja' ? 'ok' : 'accent'} size="sm">
                          {CULTURA_LABELS[r.cultura] ?? r.cultura}
                        </Chip>
                      </td>
                      <td style={{ textAlign: 'center' }}>{r.safra}</td>
                      <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {r.sc_ha != null ? r.sc_ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {r.kg_ha != null ? r.kg_ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
                      </td>
                      <td>{r.fonte ? FONTE_LABELS[r.fonte] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.tecnologia ? TECNOLOGIA_LABELS[r.tecnologia] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.cultivo ? CULTIVO_LABELS[r.cultivo] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.mao_de_obra ? MAO_DE_OBRA_LABELS[r.mao_de_obra] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.fertilidade ? FERTILIDADE_LABELS[r.fertilidade] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.agricultura ? AGRICULTURA_LABELS[r.agricultura] : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.irrigacao ? IRRIGACAO_LABELS[r.irrigacao] : '—'}</td>
                      <td style={{ textAlign: 'center' }}><StatusChip status={r.status} /></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Editar"
                          onClick={() => setModalMode({ kind: 'edit', cpf, clienteId, record: r })}
                        >
                          <Pencil size={13} strokeWidth={1.5} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Excluir"
                          onClick={() => handleDelete(r)}
                          disabled={del.isPending}
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <ProdutividadeEditModal mode={modalMode} onClose={() => setModalMode(null)} />
    </>
  );
}
